<template>
  <div class="filter-and-sort">
    <div class="filter-and-sort__toolbar">
      <button
        class="
          filter-and-sort__toolbar-trigger
          button button--block
        "
        type="button"
        @click="
          openOverlay({
            ignoreDismissed: true,
            namespace: 'filterDrawer',
          })
        "
      >
        <span
          class="button__label text-body-s-bold"
          v-text="$string('general.filter_and_sort.filter_trigger')"
        />

        <span
          v-if="activeFilters.length > 0"
          class="filter-and-sort__toolbar-trigger-count text-body-s-regular"
          v-text="$string('general.filter_and_sort.applied', {
            replace: {
              num: activeFilters.length,
            },
          })"
        />

        <span class="button__icon">
          <icon-filter />
        </span>
      </button>

      <sort-dropdown
        :current-sort="currentSort"
        :sort-options="sortOptions"
        @handle-sort="handleSort"
      />
    </div>

    <overlay
      key="filterDrawer"
      block-class="filter-drawer-overlay"
      direction="right"
      namespace="filterDrawer"
      type="drawer"
    >
      <template #body>
        <div class="filter-drawer-overlay__inner">
          <filter-dropdown
            class="container"
            :active-filters="activeFilters"
            :filters="filters"
            :is-vehicle-finder-collection="isVehicleFinderCollection"
            :price-range="priceRange"
            :showing-vehicle-results="showingVehicleResults"
            @clear-all="clearAll"
            @clear-filter="clearFilter"
            @handle-filter="handleFilter"
            @handle-price-filter="handlePriceFilter"
          />

          <div class="filter-drawer-overlay__footer container">
            <btn
              :label="
                $string('general.filter_and_sort.view_results', {
                  pluralise: totalCount || 0,
                  replace: {
                    num: totalCount || 0,
                  },
                })
              "
              modifiers="primary center block"
              type="button"
              @click="closeOverlay('filterDrawer')"
            />
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Filter and sort (filter-and-sort)
 * -----------------------------------------------------------------------------
 * Filter and sort used for collection and search pages.
 *
 * @param {Object} activeFilters - Active filters.
 * @param {Object} currentSort - Current sort option.
 * @param {Object} filters - Filters array.
 * @param {Boolean} [isVehicleFinderCollection] - Collection is either the
 * redirect or results collection.
 * @param {Object} priceRange - Price range.
 * @param {Boolean} [showingVehicleResults] - Showing vehicle finder results.
 * @param {Array} sortOptions - Sort options array.
 * @param {Number} totalCount - Number of products.
 *
 * @emits clearAll - Emit clear all.
 * @emits clearFilter - Emit clear filter.
 * @emits handleSort - Emit sort update.
 * @emits handleFilter - Emit filter update.
 * @emits handlePriceFilter - Emit price filter update.
 *
 */
import { mapActions } from 'vuex'

import Btn from '~global/btn/btn'
import FilterDropdown from './filter-dropdown'
import Overlay from '~global/overlay/overlay'
import SortDropdown from './sort-dropdown'

import IconFilter from '~icons/general/filter.svg'

export default {
  name: 'FilterAndSort',

  components: {
    Btn,
    FilterDropdown,
    IconFilter,
    Overlay,
    SortDropdown,
  },

  props: {
    activeFilters: {
      type: Array,
      default: () => [],
    },
    currentSort: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Array,
      default: () => [],
    },
    isVehicleFinderCollection: {
      type: Boolean,
      default: false,
    },
    priceRange: {
      type: Object,
      default: () => ({}),
    },
    showingVehicleResults: {
      type: Boolean,
      default: false,
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'clear-all': (payload) => typeof payload === 'object',
    'clear-filter': (payload) => typeof payload === 'object',
    'handle-filter': (payload) => typeof payload === 'object',
    'handle-price-filter': (payload) => typeof payload === 'object',
    'handle-sort': (payload) => typeof payload === 'object',
  },

  methods: {

    /**
     * Map Vuex actions.
    */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Reset filters and close filter drawer.
     * @param {Array} hiddenActiveFilters - Hidden active filters.
     */
    clearAll(hiddenActiveFilters) {
      this.$emit('clear-all', hiddenActiveFilters)
      this.closeOverlay('filterDrawer')
    },

    /**
     * Handle clear filter.
     * @param {String} filter - Filter group.
     */
    clearFilter(filter) {
      this.$emit('clear-filter', filter)
    },

    /**
     * Handle filter.
     * @param {Object} filter - Filter object.
     */
    handleFilter(filter) {
      this.$emit('handle-filter', filter)
    },

    /**
     * Handle price filter change.
     * @param {Array} value - Price range.
     */
    handlePriceFilter(value) {
      this.$emit('handle-price-filter', value)
    },

    /**
     * Handle sort change.
     */
    handleSort(selectedSort) {
      this.$emit('handle-sort', selectedSort)
    },
  },
}
</script>

<style lang="scss">
@import './filter-and-sort';
@import './filter-drawer-overlay';
</style>
